import { type UUID } from '~/types/common';

import { type PdfDownloadType } from './availablePdfTypes';
import { type DeliveryNoteSearchParams } from './types';
import { type DuplicateCheckParams } from './useQueryDeliveryNoteDuplicateCheck';
import { type DeliveryNoteReportRequest } from './useQueryDeliveryNoteReport';
import { type ListParsingCasesParams } from './useQueryParsingCases/validations';

/**
 * Query key factory for delivery note-related queries.
 */
export const queryKeysDeliveryNote = {
  availablePdfTypes: (assetId: string) =>
    [queryKeysDeliveryNote.base(), 'availablePdfTypes', { assetId }] as const,
  base: () => 'deliveryNotes' as const,
  get: (deliveryNoteId: UUID) =>
    [queryKeysDeliveryNote.base(), 'detail', { deliveryNoteId }] as const,
  getAll: (searchParams: DeliveryNoteSearchParams) =>
    [queryKeysDeliveryNote.base(), 'list', searchParams] as const,
  getAllArticleTemplates: (companyId: { companyId?: UUID }) =>
    [
      queryKeysDeliveryNote.base(),
      'listArticleTemplates',
      { companyId },
    ] as const,
  getAllInfinite: () =>
    [queryKeysDeliveryNote.base(), 'list', 'infinite'] as const,
  getAllParsingCases: (params: ListParsingCasesParams) =>
    [queryKeysDeliveryNote.base(), 'parsingCases', 'list', params] as const,
  getAnalytics: (searchParams: DeliveryNoteSearchParams) =>
    [queryKeysDeliveryNote.base(), 'analytics', searchParams] as const,
  getArticleSuggestions: (
    siteId: UUID,
    supplierId: UUID,
    limit?: number,
    query?: string,
  ) =>
    [
      queryKeysDeliveryNote.base(),
      'articleSuggestions',
      { limit, query, siteId, supplierId },
    ] as const,
  getAttachment: (chainId: UUID, digest: string) =>
    [queryKeysDeliveryNote.base(), 'attachment', { chainId, digest }] as const,
  getBulk: (deliveryNoteIds: UUID[]) => [
    queryKeysDeliveryNote.base(),
    'listBulk',
    { deliveryNoteIds },
  ],
  getChains: (deliveryNoteIds: UUID[]) =>
    [queryKeysDeliveryNote.base(), 'chains', { deliveryNoteIds }] as const,
  getContractSuggestions: (assetId: UUID) =>
    [queryKeysDeliveryNote.base(), 'contractSuggestions', { assetId }] as const,
  getDlnNumberSuggestion: () =>
    [queryKeysDeliveryNote.base(), 'dlnNumberSuggestion'] as const,
  getDuplicateCheck: (params: DuplicateCheckParams) =>
    [queryKeysDeliveryNote.base(), 'duplicateCheck', params] as const,
  getExportExcel: (requestId: UUID) =>
    [queryKeysDeliveryNote.base(), 'exportExcel', { requestId }] as const,
  getFilterSuggestions: (searchParams: DeliveryNoteSearchParams) =>
    [queryKeysDeliveryNote.base(), 'filterSuggestions', searchParams] as const,
  getHandwrittenSignature: (chainId: UUID) =>
    [
      queryKeysDeliveryNote.base(),
      'handwrittenSignature',
      { chainId },
    ] as const,
  getInternal: (assetId: UUID) =>
    [queryKeysDeliveryNote.base(), 'internal', { assetId }] as const,
  getOptions: (query: string) =>
    [queryKeysDeliveryNote.base(), 'options', { query }] as const,
  getParsingCase: (caseId: UUID) =>
    [
      queryKeysDeliveryNote.base(),
      'parsingCases',
      'detail',
      { caseId },
    ] as const,
  getParsingCaseFormConfig: () =>
    [queryKeysDeliveryNote.base(), 'parsingCases', 'formConfig'] as const,
  getPdfExport: (requestId: UUID) =>
    [queryKeysDeliveryNote.base(), 'exportPdf', { requestId }] as const,
  getPdfPreview: (deliveryNoteId: UUID, pdfType: PdfDownloadType) =>
    [
      queryKeysDeliveryNote.base(),
      'pdfPreview',
      { deliveryNoteId, pdfType },
    ] as const,
  getPermittedUserIds: (deliveryNoteId: UUID) =>
    [
      queryKeysDeliveryNote.base(),
      'permittedUsers',
      { deliveryNoteId },
    ] as const,
  getPermittedUsers: (siteIds: UUID[]) =>
    [queryKeysDeliveryNote.base(), 'permittedUser', { siteIds }] as const,
  getReport: (payload: DeliveryNoteReportRequest) =>
    [queryKeysDeliveryNote.base(), 'report', payload] as const,
  getSearchCount: (searchParams: DeliveryNoteSearchParams) =>
    [queryKeysDeliveryNote.base(), 'searchCount', searchParams] as const,
  getSharedWithUsers: (deliveryNoteId: UUID) =>
    [
      queryKeysDeliveryNote.base(),
      'sharedWithUsers',
      { deliveryNoteId },
    ] as const,
  getSignatureRequests: (deliveryNoteId: UUID) =>
    [
      queryKeysDeliveryNote.base(),
      'signatureRequests',
      { deliveryNoteId },
    ] as const,
  getSignatureRequestsBulk: (deliveryNoteIds: UUID[]) =>
    [
      queryKeysDeliveryNote.base(),
      'signatureRequestsBulk',
      { deliveryNoteIds },
    ] as const,
  getSimpleFormat: (deliveryNoteId: UUID) =>
    [queryKeysDeliveryNote.base(), 'simpleFormat', { deliveryNoteId }] as const,
  getSupplierSuggestions: (params: { limit?: number; query?: string }) =>
    [queryKeysDeliveryNote.base(), 'supplierSuggestions', params] as const,
  getSyncGatewayChanges: (since: string) =>
    [queryKeysDeliveryNote.base(), 'updatesFeed', { since }] as const,
  getTemplates: () =>
    [queryKeysDeliveryNote.base(), 'template', 'list'] as const,
  initDls: (deliveryNoteIds: UUID[]) => [
    queryKeysDeliveryNote.base(),
    'initDlns',
    { deliveryNoteIds },
  ],
} as const;
